import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, BaseMapItem } from 'types';
import { setBaseMap } from 'redux/app/actions';
import { InlineLoading, Button } from 'carbon-components-react';
import { useEventListener, useComponentMount } from 'utilities/hooks.util';
import { setIsOpenBaseMapPanel, clearBaseMaps } from 'redux/map/actions';
import { Information16 } from '@carbon/icons-react';
import BaseMapOptionItem from './item';
import { findBasemapByIdentifier } from 'utilities/basemap-migration.util';
import { debugLog, debugWarn } from 'utilities/basemap-debug.util';
import {
  addLayerToMap,
  findLayerById,
  getActiveMapForMutation
} from 'utilities/map-instance.util';

const SwitcherPanel: React.FC<Props> = ({
  baseMap,
  options,
  isOpen,
  error
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [nationOptions, setNationOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);

  const setBaseMapById = useCallback(
    (id: string, theme: string) => {
      debugLog('[Basemap Switch] Switching to basemap:', id);
      const { loadedMap, loadedMapModules } = window;
      if (loadedMap && loadedMapModules) {
        // Find basemap config from options (feature service data)
        const basemapItem = findBasemapByIdentifier(id, options);

        if (!basemapItem) {
          debugWarn(
            '[Basemap Switch] Basemap not found in options:',
            id,
            '- Available basemaps:',
            options.map((o: BaseMapItem) => o.id)
          );
          return;
        }

        const basemapConfig = {
          name: basemapItem.label,
          styleId: basemapItem.styleId,
          webmapId: basemapItem.webmapId
        };

        debugLog('[Basemap Switch] Found basemap config:', {
          id: id,
          name: basemapConfig.name,
          styleId: basemapConfig.styleId,
          webmapId: basemapConfig.webmapId
        });

        if (basemapConfig) {
          const { Basemap, BasemapStyle, WebMap } = loadedMapModules;

          // Dual loading pattern: WebMap + BasemapStyle override
          if (
            basemapConfig.styleId &&
            basemapConfig.webmapId &&
            WebMap &&
            Basemap &&
            BasemapStyle
          ) {
            debugLog(
              '[Basemap Switch] Dual loading: WebMap + BasemapStyle override'
            );
            debugLog(
              '[Basemap Switch] Loading WebMap:',
              basemapConfig.webmapId
            );
            // Load WebMap with all its layers and override basemap
            var webMap = new WebMap({
              portalItem: { id: basemapConfig.webmapId }
            });
            webMap
              .loadAll()
              .then(function () {
                debugLog('[Basemap Switch] WebMap loaded successfully');
                debugLog(
                  '[Basemap Switch] WebMap layers:',
                  webMap.layers.length
                );
                debugLog(
                  '[Basemap Switch] Overriding basemap with BasemapStyle:',
                  basemapConfig.styleId
                );

                // Override the webmap's basemap with the specified style
                webMap.basemap = new Basemap({
                  style: new BasemapStyle({ id: basemapConfig.styleId })
                });

                // Preserve important layers before replacing the map
                if (window.loadedView && window.loadedView.map) {
                  const oldMap = window.loadedView.map;

                  // List of layer IDs that MUST be preserved
                  const criticalLayerIds = [
                    'drawing-layer',
                    'cloned-shape-layer',
                    'enquiries-feature-layer',
                    'enquiries-shape-layer',
                    'enquiries-point-layer',
                    'enquiries-grid-layer',
                    'all-enquiries-shape-layer',
                    'all-enquiries-grid-layer',
                    'referrals-feature-layer',
                    'referrals-shape-layer',
                    'referrals-grid-layer'
                  ];

                  // Preserve critical layers by ID (even if hidden/not visible)
                  criticalLayerIds.forEach((layerId) => {
                    const layer = oldMap.findLayerById(layerId);
                    if (layer) {
                      debugLog('[Basemap Switch] Preserving layer:', layerId);
                      if (!webMap.findLayerById(layerId)) {
                        webMap.add(layer);
                      }
                    }
                  });

                  // Also preserve any other operational layers that aren't part of the webmap
                  // (excluding basemap layers and already-preserved critical layers)
                  oldMap.layers.forEach((layer: any) => {
                    if (
                      layer.id &&
                      !criticalLayerIds.includes(layer.id) &&
                      !layer.id.includes('basemap')
                    ) {
                      debugLog(
                        '[Basemap Switch] Preserving additional layer:',
                        layer.id
                      );
                      // Check if this layer isn't already in the new webmap
                      if (!webMap.findLayerById(layer.id)) {
                        webMap.add(layer);
                      }
                    }
                  });
                }

                // Replace the entire map with the webmap
                if (window.loadedView) {
                  window.loadedView.map = webMap;
                  // Also update window.loadedMap to keep it in sync
                  window.loadedMap = webMap;
                }

                debugLog(
                  '[Basemap Switch] Dual loading complete - WebMap with style override'
                );
              })
              .catch(function (error: any) {
                debugWarn(
                  '[Basemap Switch] WebMap load failed, falling back to style:',
                  error
                );

                // Preserve drawing layer and other operational layers before changing basemap
                const preservedLayers: any[] = [];
                if (window.loadedView && window.loadedView.map) {
                  const currentMap = window.loadedView.map;
                  currentMap.layers.forEach((layer: any) => {
                    if (
                      layer.id &&
                      (layer.id === 'drawing-layer' ||
                        !layer.id.includes('basemap'))
                    ) {
                      debugLog('[Basemap Switch] Preserving layer:', layer.id);
                      preservedLayers.push(layer);
                    }
                  });
                }

                // Fallback to just using the style
                loadedMap.basemap = new Basemap({
                  style: new BasemapStyle({ id: basemapConfig.styleId })
                });

                // Re-add preserved layers after basemap change
                preservedLayers.forEach((layer: any) => {
                  // Use centralized utility to check and add layer
                  if (!findLayerById(layer.id)) {
                    debugLog(
                      '[Basemap Switch] Re-adding preserved layer:',
                      layer.id
                    );
                    addLayerToMap(layer);
                  }
                });
              });
          } else if (basemapConfig.styleId && Basemap && BasemapStyle) {
            // Use BasemapStyle only for style-based basemaps
            debugLog(
              '[Basemap Switch] Using BasemapStyle:',
              basemapConfig.styleId
            );

            // Preserve drawing layer and other operational layers before changing basemap
            const preservedLayers: any[] = [];
            if (window.loadedView && window.loadedView.map) {
              const currentMap = window.loadedView.map;
              currentMap.layers.forEach((layer: any) => {
                if (
                  layer.id &&
                  (layer.id === 'drawing-layer' ||
                    !layer.id.includes('basemap'))
                ) {
                  debugLog('[Basemap Switch] Preserving layer:', layer.id);
                  preservedLayers.push(layer);
                }
              });
            }

            loadedMap.basemap = new Basemap({
              style: new BasemapStyle({ id: basemapConfig.styleId })
            });

            // Re-add preserved layers after basemap change
            preservedLayers.forEach((layer: any) => {
              // Use centralized utility to check and add layer
              if (!findLayerById(layer.id)) {
                debugLog(
                  '[Basemap Switch] Re-adding preserved layer:',
                  layer.id
                );
                addLayerToMap(layer);
              }
            });
          } else if (basemapConfig.webmapId && Basemap) {
            // Use Basemap with portalItem for webmap-only basemaps (e.g., state basemaps, OSM)
            debugLog(
              '[Basemap Switch] Using Basemap with portalItem (webmap):',
              basemapConfig.webmapId
            );

            // Preserve drawing layer and other operational layers before changing basemap
            const preservedLayers: any[] = [];
            if (window.loadedView && window.loadedView.map) {
              const currentMap = window.loadedView.map;
              currentMap.layers.forEach((layer: any) => {
                if (
                  layer.id &&
                  (layer.id === 'drawing-layer' ||
                    !layer.id.includes('basemap'))
                ) {
                  debugLog('[Basemap Switch] Preserving layer:', layer.id);
                  preservedLayers.push(layer);
                }
              });
            }

            // Create a Basemap instance from the webmap portal item
            var webmapBasemap = new Basemap({
              portalItem: { id: basemapConfig.webmapId }
            });

            // Load the basemap first before setting it on the map
            webmapBasemap
              .load()
              .then(function () {
                debugLog('[Basemap Switch] Webmap basemap loaded successfully');
                const targetMap = getActiveMapForMutation() ?? loadedMap;
                if (!targetMap) {
                  debugWarn(
                    '[Basemap Switch] No target map available for basemap update'
                  );
                  return;
                }
                targetMap.basemap = webmapBasemap;

                // Re-add preserved layers after basemap change
                preservedLayers.forEach((layer: any) => {
                  if (!targetMap.findLayerById(layer.id)) {
                    debugLog(
                      '[Basemap Switch] Re-adding preserved layer:',
                      layer.id
                    );
                    targetMap.add(layer);
                  }
                });
              })
              .catch(function (error: any) {
                debugWarn(
                  '[Basemap Switch] Webmap basemap load failed:',
                  error
                );
                const targetMap = getActiveMapForMutation() ?? loadedMap;
                if (!targetMap) {
                  debugWarn(
                    '[Basemap Switch] No target map available for basemap update'
                  );
                  return;
                }
                targetMap.basemap = webmapBasemap;

                // Re-add preserved layers even on error
                preservedLayers.forEach((layer: any) => {
                  if (!targetMap.findLayerById(layer.id)) {
                    debugLog(
                      '[Basemap Switch] Re-adding preserved layer:',
                      layer.id
                    );
                    targetMap.add(layer);
                  }
                });
              });
          }
        } else {
          // Fallback to portalItem if config not found
          debugLog(
            '[Basemap Switch] No config found, using basemapId directly'
          );

          // Preserve drawing layer and other operational layers before changing basemap
          const preservedLayers: any[] = [];
          if (window.loadedView && window.loadedView.map) {
            const currentMap = window.loadedView.map;
            currentMap.layers.forEach((layer: any) => {
              if (
                layer.id &&
                (layer.id === 'drawing-layer' || !layer.id.includes('basemap'))
              ) {
                debugLog('[Basemap Switch] Preserving layer:', layer.id);
                preservedLayers.push(layer);
              }
            });
          }

          const { Basemap } = loadedMapModules;
          if (Basemap) {
            loadedMap.basemap = new Basemap({
              portalItem: { id }
            });

            // Re-add preserved layers after basemap change
            preservedLayers.forEach((layer: any) => {
              // Use centralized utility to check and add layer
              if (!findLayerById(layer.id)) {
                debugLog(
                  '[Basemap Switch] Re-adding preserved layer:',
                  layer.id
                );
                addLayerToMap(layer);
              }
            });
          }
        }
      }
      dispatch(setBaseMap(id, theme));
      dispatch(setIsOpenBaseMapPanel(false));
    },
    [options, dispatch]
  );

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          dispatch(setIsOpenBaseMapPanel(false));
        }
      }
    },
    [containerRef, dispatch]
  );

  useEffect(() => {
    const nationOptions = options.filter((o: any) => o.category === 1);
    const stateOptions = options.filter((o: any) => o.category !== 1);
    setNationOptions(nationOptions);
    setStateOptions(stateOptions);
  }, [options]);

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  useComponentMount(() => {
    return () => {
      error && dispatch(clearBaseMaps());
    };
  });

  return (
    <>
      {isOpen && (
        <div className="base-map-switcher-modal">
          <div className="base-map-switcher-panel" ref={containerRef}>
            <div className="base-map-switcher-panel-header">
              <div className="label">Map style</div>
              <Button
                size="small"
                kind="ghost"
                onClick={() => dispatch(setIsOpenBaseMapPanel(false))}
              >
                Close
              </Button>
            </div>
            <div className="base-map-switcher-panel-body custom-scroller">
              {options.length > 0 ? (
                <>
                  <div className="nation-base-map-container">
                    {nationOptions.map((item: BaseMapItem, inx: number) => {
                      return (
                        <BaseMapOptionItem
                          key={inx}
                          item={item}
                          baseMap={baseMap}
                          setBaseMapById={setBaseMapById}
                        />
                      );
                    })}
                  </div>
                  <div className="state-base-map-container">
                    {stateOptions.map((item: BaseMapItem, inx: number) => {
                      return (
                        <BaseMapOptionItem
                          key={inx}
                          item={item}
                          baseMap={baseMap}
                          setBaseMapById={setBaseMapById}
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="base-map-switcher-loading">
                  <InlineLoading description="Loading basemaps ..." />
                </div>
              )}
            </div>
            <div className="base-map-switcher-panel-footer">
              <Information16 />
              <div className="text">
                Styles outside current extent are disabled
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = ({ app, mapCache }: RootState) => ({
  baseMap: app.selectedBasemap,
  options: mapCache.basemapOptions,
  isOpen: mapCache.IsOpenBaseMapPanel,
  error: mapCache.basemapError
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default connector(SwitcherPanel);
